import { useEffect, useId, useState } from "react";
import { Calendar } from "@/shared/ui/calendar";
import { format, isAfter, isValid, parse, type Locale } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { cn } from "@/shared/lib/utils";

interface RangeInputPickerLabels {
  startDate: string;
  endDate: string;
  cancel: string;
  apply: string;
  placeholder: string;
  invalidOrder: string;
}

interface RangeInputPickerProps {
  draft: DateRange | undefined;
  onDraftChange: (range: DateRange | undefined) => void;
  onApply: (range: DateRange | undefined) => void;
  onCancel: () => void;
  initialMonth?: Date;
  labels: RangeInputPickerLabels;
  calendarClassNames?: Record<string, string>;
  locale?: Locale;
}

const DATE_FORMAT = "dd/MM/yyyy";

function formatInput(date: Date | undefined, locale: Locale): string {
  if (!date) return "";
  return format(date, DATE_FORMAT, { locale });
}

function parseInput(value: string, locale: Locale): Date | null {
  if (!value || value.length !== 10) return null;
  const parsed = parse(value, DATE_FORMAT, new Date(), { locale });
  return isValid(parsed) ? parsed : null;
}

function maskDate(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts: string[] = [];
  if (digits.length >= 1) parts.push(digits.slice(0, 2));
  if (digits.length >= 3) parts.push(digits.slice(2, 4));
  if (digits.length >= 5) parts.push(digits.slice(4, 8));
  return parts.join("/");
}

export function RangeInputPicker({
  draft,
  onDraftChange,
  onApply,
  onCancel,
  initialMonth,
  labels,
  calendarClassNames,
  locale = ptBR,
}: RangeInputPickerProps) {
  const reactId = useId();
  const fromId = `range-start-input-${reactId}`;
  const toId = `range-end-input-${reactId}`;
  const errorId = `range-error-${reactId}`;
  const [fromInput, setFromInput] = useState<string>(() =>
    formatInput(draft?.from, locale)
  );
  const [toInput, setToInput] = useState<string>(() =>
    formatInput(draft?.to, locale)
  );
  const [month, setMonth] = useState<Date | undefined>(
    initialMonth ?? draft?.from ?? new Date()
  );

  // Sincroniza inputs quando draft muda externamente (preset/limpar/sync de pai)
  useEffect(() => {
    setFromInput(formatInput(draft?.from, locale));
    setToInput(formatInput(draft?.to, locale));
    if (draft?.from) {
      setMonth((prev) =>
        !prev || prev.getTime() !== draft.from!.getTime() ? draft.from : prev
      );
    }
  }, [draft?.from, draft?.to, locale]);

  const handleSelect = (range: DateRange | undefined) => {
    onDraftChange(range);
    if (range?.from) setMonth(range.from);
  };

  const handleFromBlur = () => {
    const parsed = parseInput(fromInput, locale);
    if (parsed) {
      onDraftChange({ from: parsed, to: draft?.to });
      setMonth(parsed);
    } else if (fromInput === "") {
      onDraftChange({ from: undefined, to: draft?.to });
    } else {
      setFromInput(formatInput(draft?.from, locale));
    }
  };

  const handleToBlur = () => {
    const parsed = parseInput(toInput, locale);
    if (parsed) {
      onDraftChange({ from: draft?.from, to: parsed });
    } else if (toInput === "") {
      onDraftChange({ from: draft?.from, to: undefined });
    } else {
      setToInput(formatInput(draft?.to, locale));
    }
  };

  const isInvalidOrder =
    !!draft?.from && !!draft?.to && isAfter(draft.from, draft.to);
  const canApply = !!draft?.from && !!draft?.to && !isInvalidOrder;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && canApply) {
      e.preventDefault();
      onApply(draft);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="select-none flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <label
            htmlFor={fromId}
            className="text-[0.65rem] font-medium uppercase tracking-wide text-white/45"
          >
            {labels.startDate}
          </label>
          <input
            id={fromId}
            type="text"
            inputMode="numeric"
            value={fromInput}
            placeholder={labels.placeholder}
            onChange={(e) => setFromInput(maskDate(e.target.value))}
            onBlur={handleFromBlur}
            onKeyDown={handleKeyDown}
            aria-invalid={isInvalidOrder ? "true" : undefined}
            aria-errormessage={isInvalidOrder ? errorId : undefined}
            className={cn(
              "h-8 px-2.5 rounded-md text-xs bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/30",
              "focus:outline-none focus:ring-1 focus:ring-primary/60 focus:border-primary/60",
              isInvalidOrder && "border-red-500/60"
            )}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor={toId}
            className="text-[0.65rem] font-medium uppercase tracking-wide text-white/45"
          >
            {labels.endDate}
          </label>
          <input
            id={toId}
            type="text"
            inputMode="numeric"
            value={toInput}
            placeholder={labels.placeholder}
            onChange={(e) => setToInput(maskDate(e.target.value))}
            onBlur={handleToBlur}
            onKeyDown={handleKeyDown}
            aria-invalid={isInvalidOrder ? "true" : undefined}
            aria-errormessage={isInvalidOrder ? errorId : undefined}
            className={cn(
              "h-8 px-2.5 rounded-md text-xs bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/30",
              "focus:outline-none focus:ring-1 focus:ring-primary/60 focus:border-primary/60",
              isInvalidOrder && "border-red-500/60"
            )}
          />
        </div>
      </div>

      {isInvalidOrder && (
        <p
          id={errorId}
          role="alert"
          className="px-1 text-[0.7rem] text-red-400"
        >
          {labels.invalidOrder}
        </p>
      )}

      <div className="flex justify-center">
        <Calendar
          mode="range"
          selected={draft}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          numberOfMonths={1}
          locale={locale}
          showOutsideDays={false}
          classNames={calendarClassNames}
          className="[--cell-size:2.25rem] [--rdp-day_button-border:0px] [--rdp-selected-border:0px] p-0"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 pt-3">
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            "h-8 rounded-md text-xs font-medium transition-colors",
            "bg-white/[0.04] text-white/70 hover:bg-white/[0.08] hover:text-white"
          )}
        >
          {labels.cancel}
        </button>
        <button
          type="button"
          disabled={!canApply}
          onClick={() => onApply(draft)}
          className={cn(
            "h-8 rounded-md text-xs font-medium transition-colors",
            "bg-primary text-primary-foreground hover:bg-primary/90",
            "disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
          )}
        >
          {labels.apply}
        </button>
      </div>
    </div>
  );
}
